import { useCallback, useState } from "react";
import { useAtomValue } from "jotai";
import { GetContextMenuItemsParams, MenuItemDef } from "ag-grid-community";
import { AccountAtom } from "../../store";
import { useNotifications } from "../../hooks/useNotifications";
import { deleteCashFlow, hideCashflows, unhideCashflows } from "../../api/Cashflow";
import { CashFlowEx } from "../../types/Cashflow";

interface UseCashflowContextMenuController {
  getContextMenuItems: (
    params: GetContextMenuItemsParams
  ) => (string | MenuItemDef)[];
}

interface UseCashflowContextMenuProps {
  onCashflowsDeleted?: () => void;
}

export const useCashflowContextMenuController = (
  props?: UseCashflowContextMenuProps
): UseCashflowContextMenuController => {
  const accountState = useAtomValue(AccountAtom);
  const notifications = useNotifications();

  const getSelectedCashflows = (params: GetContextMenuItemsParams): CashFlowEx[] => {
    const selectedRows = params.api.getSelectedRows();
    const cashflows: CashFlowEx[] = [];

    if (selectedRows.length > 0) {
      // If we have selected rows, use them
      selectedRows.forEach((row) => {
        if (row.id || row.cashflowId) {
          cashflows.push(row);
        }
      });
    } else if (params.node?.data) {
      // If no selected rows but we have a node data, use that
      if (params.node.data.id || params.node.data.cashflowId) {
        cashflows.push(params.node.data);
      }
    }

    return cashflows;
  };

  const handleDeleteCashflows = useCallback(
    async (params: GetContextMenuItemsParams) => {
      const selectedCashflows = getSelectedCashflows(params);
      
      if (selectedCashflows.length === 0) {
        notifications.showErrorMessage("No cashflows selected for deletion");
        return;
      }

      const confirmMessage = selectedCashflows.length === 1
        ? "Are you sure you want to delete this cashflow? This action cannot be undone."
        : `Are you sure you want to delete ${selectedCashflows.length} cashflows? This action cannot be undone.`;

      if (!window.confirm(confirmMessage)) {
        return;
      }

      if (!accountState?.selectedAccount?.id) {
        notifications.showErrorMessage("No account selected");
        return;
      }

      try {
        notifications.createNotification(
          "Deleting cashflows...",
          `Please wait while we delete ${selectedCashflows.length === 1 ? 'the cashflow' : `${selectedCashflows.length} cashflows`}`
        );

        // Delete each cashflow
        const deletePromises = selectedCashflows.map(cashflow => {
          const cashflowId = cashflow.id || cashflow.cashflowId;
          return deleteCashFlow(cashflowId, accountState.selectedAccount!.id);
        });

        await Promise.all(deletePromises);

        notifications.clearNotifications();
        notifications.createTradeChangeNotification(
          `${selectedCashflows.length === 1 ? 'Cashflow' : 'Cashflows'} deleted successfully`
        );

        // Trigger refresh callback if provided
        if (props?.onCashflowsDeleted) {
          props.onCashflowsDeleted();
        }

      } catch (error) {
        notifications.clearNotifications();
        notifications.showErrorMessage(
          error instanceof Error ? error.message : "Failed to delete cashflows",
          "Delete Error"
        );
      }
    },
    [accountState, notifications, props]
  );

  const handleHideCashflows = useCallback(
    async (params: GetContextMenuItemsParams) => {
      const selectedCashflows = getSelectedCashflows(params);
      
      if (selectedCashflows.length === 0) {
        notifications.showErrorMessage("No cashflows selected for hiding");
        return;
      }

      const confirmMessage = selectedCashflows.length === 1
        ? "Are you sure you want to hide this cashflow? It will no longer appear in reports and analysis."
        : `Are you sure you want to hide ${selectedCashflows.length} cashflows? They will no longer appear in reports and analysis.`;

      if (!window.confirm(confirmMessage)) {
        return;
      }

      if (!accountState?.selectedAccount?.id) {
        notifications.showErrorMessage("No account selected");
        return;
      }

      try {
        notifications.createNotification(
          "Hiding cashflows...",
          `Please wait while we hide ${selectedCashflows.length === 1 ? 'the cashflow' : `${selectedCashflows.length} cashflows`}`
        );

        // Get cashflow IDs
        const cashflowIds = selectedCashflows.map(cashflow => 
          cashflow.id || cashflow.cashflowId
        );

        // Hide the cashflows using specific API function
        await hideCashflows(accountState.selectedAccount.id, cashflowIds);

        notifications.clearNotifications();
        notifications.createTradeChangeNotification(
          `${selectedCashflows.length === 1 ? 'Cashflow' : 'Cashflows'} hidden successfully`
        );

        // Trigger refresh callback if provided
        if (props?.onCashflowsDeleted) {
          props.onCashflowsDeleted();
        }

      } catch (error) {
        notifications.clearNotifications();
        notifications.showErrorMessage(
          error instanceof Error ? error.message : "Failed to hide cashflows",
          "Hide Error"
        );
      }
    },
    [accountState, notifications, props]
  );

  const handleUnhideCashflows = useCallback(
    async (params: GetContextMenuItemsParams) => {
      const selectedCashflows = getSelectedCashflows(params);
      
      if (selectedCashflows.length === 0) {
        notifications.showErrorMessage("No cashflows selected for unhiding");
        return;
      }

      const confirmMessage = selectedCashflows.length === 1
        ? "Are you sure you want to unhide this cashflow? It will appear in reports and analysis again."
        : `Are you sure you want to unhide ${selectedCashflows.length} cashflows? They will appear in reports and analysis again.`;

      if (!window.confirm(confirmMessage)) {
        return;
      }

      if (!accountState?.selectedAccount?.id) {
        notifications.showErrorMessage("No account selected");
        return;
      }

      try {
        notifications.createNotification(
          "Unhiding cashflows...",
          `Please wait while we unhide ${selectedCashflows.length === 1 ? 'the cashflow' : `${selectedCashflows.length} cashflows`}`
        );

        // Get cashflow IDs
        const cashflowIds = selectedCashflows.map(cashflow => 
          cashflow.id || cashflow.cashflowId
        );

        // Unhide the cashflows using specific API function
        await unhideCashflows(accountState.selectedAccount.id, cashflowIds);

        notifications.clearNotifications();
        notifications.createTradeChangeNotification(
          `${selectedCashflows.length === 1 ? 'Cashflow' : 'Cashflows'} unhidden successfully`
        );

        // Trigger refresh callback if provided
        if (props?.onCashflowsDeleted) {
          props.onCashflowsDeleted();
        }

      } catch (error) {
        notifications.clearNotifications();
        notifications.showErrorMessage(
          error instanceof Error ? error.message : "Failed to unhide cashflows",
          "Unhide Error"
        );
      }
    },
    [accountState, notifications, props]
  );

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
      const selectedCashflows = getSelectedCashflows(params);
      const hasSelection = selectedCashflows.length > 0;
      
      // Check if any selected cashflows are hidden
      const hasHiddenCashflows = selectedCashflows.some(cashflow => cashflow.hidden === true);
      const hasVisibleCashflows = selectedCashflows.some(cashflow => cashflow.hidden !== true);

      const result: (string | MenuItemDef)[] = [];

      // Add hide option only if we have visible cashflows
      if (hasVisibleCashflows) {
        result.push({
          name: hasSelection && selectedCashflows.length > 1 
            ? `Hide ${selectedCashflows.length} cashflows` 
            : "Hide cashflow",
          disabled: !hasSelection,
          action: () => {
            handleHideCashflows(params);
          },
        });
      }

      // Add unhide option only if we have hidden cashflows
      if (hasHiddenCashflows) {
        result.push({
          name: hasSelection && selectedCashflows.length > 1 
            ? `Unhide ${selectedCashflows.length} cashflows` 
            : "Unhide cashflow",
          disabled: !hasSelection,
          action: () => {
            handleUnhideCashflows(params);
          },
        });
      }

      if (result.length > 0) {
        result.push("separator");
      }

      result.push({
        name: hasSelection && selectedCashflows.length > 1 
          ? `Delete ${selectedCashflows.length} cashflows` 
          : "Delete cashflow",
        disabled: !hasSelection,
        action: () => {
          handleDeleteCashflows(params);
        },
      });

      return result;
    },
    [handleDeleteCashflows, handleHideCashflows, handleUnhideCashflows]
  );

  return {
    getContextMenuItems,
  };
}; 